import find from 'lodash/find';
import get from 'lodash/get';
import includes from 'lodash/includes';
import { Component, ReactNode } from 'react';

import { logError } from '@savgroup-front-common/configuration/appInsights/AppInsights';
import { BUTTON_TYPES } from '@savgroup-front-common/constants';
import { Button } from '@savgroup-front-common/core/atoms/button';
import Input from '@savgroup-front-common/core/atoms/Form/Input';
import { InputGroup } from '@savgroup-front-common/core/atoms/Form/InputGroup';
import { Loader } from '@savgroup-front-common/core/atoms/loader';
import {
  geocodeByAddress,
  getLatLng,
  PlacesAutocomplete,
  type Suggestion,
} from '@savgroup-front-common/core/atoms/ReactPlacesAutocomplete';
import { safeFormattedIntlString } from '@savgroup-front-common/core/formatters';
import { formatSimpleAddressOneLine } from '@savgroup-front-common/core/formatters/address';
import { PositionIcon } from '@savgroup-front-common/core/protons/icons';

import { AddressSelected } from '../PickupPointSelector.types';

import { $DropdownSuggestions, $Suggestion } from './Address.styles';
import messages from './messages';

interface Location {
  lat: number;
  lng: number;
}

interface AddressProps {
  searchAddress?: { address?: string };
  onError?: (error: unknown) => void;
  onAddressSelected?: (details: AddressSelected) => void;
  isAdvancedOptionShown?: boolean;
  maxDistance: number;
  getCountry: (countryCode: string) => string;
}

interface AddressState {
  loading: boolean;
  placeId: string;
  isGoogleAddress: boolean;
  address?: string;
  isGettingCurrentLocation?: boolean;
}

class Address extends Component<AddressProps, AddressState> {
  state: AddressState = {
    loading: false,
    placeId: '',
    isGoogleAddress: false,
  };

  componentDidMount() {
    const { searchAddress } = this.props;

    if (searchAddress) {
      this.callbackAddressSelected(searchAddress);
    }
  }

  onError = (error: unknown) => {
    logError(error);
  };

  onSuccess = (pos: GeolocationPosition) => {
    const crd = pos.coords;
    const latLng: Location = { lat: crd.latitude, lng: crd.longitude };
    const geocoder = new window.google.maps.Geocoder();

    if (!window.google || !window.google.maps) {
      alert(
        'Votre navigateur est trop ancien. Merci d’utiliser une version récente.',
      );

      return Promise.resolve([]);
    }

    geocoder.geocode({ location: latLng }, (results, status) => {
      if (status === 'OK') {
        const address = results[0];

        if (address) {
          const countryCode = get(
            find(address.address_components, (info) =>
              includes(get(info, 'types'), 'country'),
            ),
            'short_name',
          );
          const postalCode = get(
            find(address.address_components, (info) =>
              includes(get(info, 'types'), 'postal_code'),
            ),
            'short_name',
          );
          const city = get(
            find(address.address_components, (info) =>
              includes(get(info, 'types'), 'locality'),
            ),
            'short_name',
          );

          this.handleOnLocalize(
            address.formatted_address,
            countryCode,
            postalCode,
            city,
          );
        } else {
          logError('No results found');
        }
      } else {
        logError(`Geocoder failed due to: ${status}`);
      }
    });
  };

  getCurrentPosition = async () => {
    this.setState(() => ({ isGettingCurrentLocation: true }));
    const options: PositionOptions = {
      enableHighAccuracy: true,
      timeout: 10000,
      maximumAge: 0,
    };

    return new Promise<GeolocationPosition>((resolve, reject) =>
      navigator.geolocation.getCurrentPosition(resolve, reject, options),
    )
      .then((data) => {
        this.setState(() => ({ isGettingCurrentLocation: false }));

        return this.onSuccess(data);
      })
      .catch((e) => {
        this.setState(() => ({ isGettingCurrentLocation: false }));

        return logError(e);
      });
  };

  handleOnLocalize = (
    address: string,
    countryCode?: string,
    postalCode?: string,
    city?: string,
  ) => {
    this.setState({
      address,
    });
    if (address) {
      this.callbackAddressSelected({ address, countryCode, postalCode, city });
    }
  };

  handleChange = (address: string) => {
    this.setState({
      address,
    });
  };

  handleSelect = (
    address: string,
    placeId?: string,
    isGoogleAddress = false,
  ) => {
    this.setState({
      loading: true,
      placeId: placeId ?? '',
      isGoogleAddress,
      address,
    });
    const { onAddressSelected } = this.props;

    geocodeByAddress(address)
      .then((results: any) => {
        getLatLng(results[0]).then(({ lat, lng }: any) => {
          this.setState({
            loading: false,
          });
          const country = results[0].address_components.find((c: any) =>
            c.types.some((t: any) => t === 'country'),
          );
          const locality = results[0].address_components.find((c: any) =>
            c.types.some((t: any) => t === 'locality'),
          );
          const postalCode = results[0].address_components.find((c: any) =>
            c.types.some((t: any) => t === 'postal_code'),
          );

          const countryCode = country ? country.short_name : undefined;

          if (countryCode && onAddressSelected) {
            onAddressSelected({
              address,
              location: { lat, lng },
              placeId,
              countryCode,
              city: locality ? locality.long_name : undefined,
              postalCode: postalCode ? postalCode.short_name : undefined,
            });
          } else {
            logError('APP-MissingCountryCode', {
              countryCode,
              address,
              lat,
              lng,
            });
          }
        });
      })
      .catch(() => {
        this.setState({
          loading: false,
        });
      });
  };

  callbackAddressSelected = ({
    address,
    countryCode,
    city,
    postalCode,
  }: {
    address?: string;
    countryCode?: string;
    postalCode?: string;
    city?: string;
  }) => {
    const { onAddressSelected } = this.props;

    if (!address || !countryCode) {
      return;
    }

    geocodeByAddress(address)
      .then((results) => getLatLng(results[0]))
      .then((latLng) => {
        if (onAddressSelected) {
          onAddressSelected({
            address,
            location: latLng,
            countryCode,
            postalCode,
            city,
          });
        }
      })
      .catch((error) => logError('Error in Address UI Component', error));
  };

  render(): ReactNode {
    const { onError, getCountry } = this.props;
    const { isGettingCurrentLocation } = this.state;

    return (
      <PlacesAutocomplete
        value={
          get(
            this.state,
            'address',
            formatSimpleAddressOneLine(
              get(this.props, ['searchAddress']),
              getCountry,
            ),
          ) || ''
        }
        onSelect={this.handleSelect}
        onChange={this.handleChange}
        onError={onError}
      >
        {({ getInputProps, suggestions, getSuggestionItemProps, loading }) => (
          <>
            <InputGroup>
              <Input
                {...getInputProps()}
                isLoading={isGettingCurrentLocation ?? false}
                selectAllOnFocus
                placeholder={messages.searchPlaces}
              />
              <Button
                primary
                hollow
                icon={<PositionIcon />}
                onClick={this.getCurrentPosition}
                type={BUTTON_TYPES.BUTTON}
                title={safeFormattedIntlString(messages.aroundMe)}
              />
            </InputGroup>

            {suggestions.length > 0 ? (
              <$DropdownSuggestions>
                {loading && (
                  <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <Loader />
                  </div>
                )}
                {suggestions.map((suggestion: Suggestion) => (
                  <$Suggestion
                    {...getSuggestionItemProps(suggestion)}
                    active={suggestion.active}
                    key={suggestion.description}
                  >
                    {suggestion.description}
                  </$Suggestion>
                ))}
              </$DropdownSuggestions>
            ) : null}
          </>
        )}
      </PlacesAutocomplete>
    );
  }
}

export default Address;
